import "./accordion";

import { createGraphAnimator, drawGraph } from "@rmlibrary/graph";
import { debounce, throttle } from "./utilities";
import {
    drawAnimatedGraph,
    getBestAndWorstGraphAxesLimits,
    getBestAndWorstGraphCSS,
    getBestAndWorstGraphDataset,
    getBestAndWorstGraphOptions,
    getInvestmentGrowthGraphCSS,
    getInvestmentGrowthGraphDataset,
    getInvestmentGrowthGraphOptions,
    getPositiveNegativeReturnsGraphConfig,
    getPositiveNegativeReturnsGraphSlices,
    getTimeToRecoverGraphCSS,
    getTimeToRecoverGraphDataset,
    getTimeToRecoverGraphOptions
} from "./graphing";
import {
    filterObjectByKeys,
    getLanguage,
    getRGBFromHex,
    getTextWidth,
    getTimestampFromDate,
    getValueFromInput,
    hideError,
    isIE,
    pluralizeText,
    showError,
    showPopup
} from "./utilities";
import { generatePDF, setupPreview } from "./pdf";

import $ from "jquery";
import Formatter from "@rmlibrary/formatting";
import Path2D from "@rmlibrary/path2d";
import { RMNumberInput } from "@rmlibrary/inputs";
import { calculateResults } from "./calculations";
import { initializePie } from "@rmlibrary/pie-graph";
import { populatePDF } from "./print";
import { storePrintValues } from "./storage";

const assetMixesInfo = [
    {
        colours: {
            dark: "#016DFF",
            light: "#a1a1a1",
            print: "blue"
        },
        image: "fixed-100.png",
        label: { en: "100% Fixed Income", fr: "100 % Revenu fixe" }
    },
    {
        colours: {
            dark: "#AB98ED",
            light: "#D7CDF7",
            print: "violet"
        },
        image: "fixed-80-20.png",
        label: { en: "80% Fixed Income / 20% Equities", fr: "80 % Revenu fixe / 20 % Actions" }
    },
    {
        colours: {
            dark: "#83A6BE",
            light: "#A9D4FF",
            print: "steel2"
        },
        image: "fixed-60-40.png",
        label: { en: "60% Fixed Income / 40% Equities", fr: "60 % Revenu fixe / 40 % Actions" }
    },
    {
        colours: {
            dark: "#03BD74",
            light: "#70F6C3",
            print: "green"
        },
        image: "50-50.png",
        label: { en: "50% Fixed Income / 50% Equities", fr: "50 % Revenu fixe / 50 % Actions" }
    },
    {
        colours: {
            dark: "#FF7E00",
            light: "#FFABA2",
            print: "coral"
        },
        image: "equity-60-40.png",
        label: { en: "40% Fixed Income / 60% Equities", fr: "40 % Revenu fixe / 60 % Actions" }
    },
    {
        colours: {
            dark: "#3f586c",
            light: "#CAEAFF",
            print: "steel"
        },
        image: "equity-20-80.png",
        label: { en: "20% Fixed Income / 80% Equities", fr: "20 % Revenu fixe / 80 % Actions" }
    },
    {
        colours: {

            dark: "#161616",
            light: "#666666",
            print: "gray"
        },
        image: "equity-100.png",
        label: { en: "100% Equities", fr: "100 % Actions" }
    }
];
const formatter = new Formatter();
const lang = getLanguage();
const monthsInAYear = 12;

const allTextInputs = $("input[type='text'].numeric");
const investmentAmountInput = $("#investment-growth-investment-amount");
const bestAndWorstTimePeriodDropdown = $("#best-worst-time-period");
const timeToRecoverTimePeriodDropdown = $("#time-to-recover-time-period");
const investmentGrowthGraph = $("#investment-growth-graph");
const bestAndWorstGraph = $("#best-and-worst-graph");
const timeToRecoverGraph = $("#time-to-recover-graph");
const assetMixOnePositiveNegativeYearsGraph = $("#asset-mix-one-positive-negative-years-graph");
const assetMixTwoPositiveNegativeYearsGraph = $("#asset-mix-two-positive-negative-years-graph");
const pdfReportButton = $("#pdf-btn");
const printReportButton = $("#print-btn");

const investmentGrowthGraphPrint = $("<canvas/>");
const assetMixOneBestAndWorstGraphPrint = $("<canvas/>");
const assetMixTwoBestAndWorstGraphPrint = $("<canvas/>");
const timeToRecoverGraphPrint = $("<canvas/>");

const investmentGrowthGraphTable = document.querySelector(".investment-growth-graph-container > .table");
const bestAndWorstGraphTable = document.querySelector(".best-and-worst-graph-container > .table");
const timeToRecoverGraphTable = document.querySelector(".time-to-recover-graph-container > .table");
const assetMixOnePositiveNegativeReturnsTable = document.querySelector(
    ".asset-mix-one-positive-negative-years-graph-container > .table"
);
const assetMixTwoPositiveNegativeReturnsTable = document.querySelector(
    ".asset-mix-two-positive-negative-years-graph-container > .table"
);

export const InvestmentGrowthGraphGlobal = {
    graphAnimator: undefined,
    css: undefined,
    options: undefined,
    dataset: undefined,
    initialDraw: true,
    initialTimeout: 500,
    reanimateAllLines: true,
    legendPaths: {},
    accessibilityTable: investmentGrowthGraphTable ? investmentGrowthGraphTable : undefined,
    previousColours: []
};

export const BestAndWorstGraphGlobal = {
    graphAnimator: undefined,
    css: undefined,
    options: undefined,
    dataset: undefined,
    initialDraw: true,
    initialTimeout: 500,
    accessibilityTable: bestAndWorstGraphTable ? bestAndWorstGraphTable : undefined
};

export const TimeToRecoverGraphGlobal = {
    graphAnimator: undefined,
    css: undefined,
    options: undefined,
    dataset: undefined,
    initialDraw: true,
    reanimateAllLines: true,
    initialTimeout: 500,
    legendPaths: {},
    accessibilityTable: timeToRecoverGraphTable ? timeToRecoverGraphTable : undefined,
    previousColours: []
};

const PositiveNegativeSlices = [];

const validators = {
    investmentAmount: (eventType) => {
        const { max, min, value } = investmentAmountInput.get(0);
        const parsedMax = formatter.parseNumber(max);
        const parsedMin = formatter.parseNumber(min);
        let investmentAmount = value;
        let isValid = false;

        try {
            investmentAmount = formatter.parseNumber(investmentAmount);
        } catch (error) {
            showError("investment-growth-investment-amount-error", eventType);
            showError("best-and-worst-investment-amount-error", eventType);
            showError("time-to-recover-investment-amount-error", eventType);
            return isValid;
        }

        if (isNaN(investmentAmount) || investmentAmount < parsedMin || investmentAmount > parsedMax) {
            showError("investment-growth-investment-amount-error", eventType);
            showError("best-and-worst-investment-amount-error", eventType);
            showError("time-to-recover-investment-amount-error", eventType);
            pdfReportButton.css("opacity", "0.5");
            printReportButton.css("opacity", "0.5");
        } else {
            hideError("investment-growth-investment-amount-error");
            hideError("best-and-worst-investment-amount-error");
            hideError("time-to-recover-investment-amount-error");
            pdfReportButton.css("opacity", "1");
            printReportButton.css("opacity", "1");
            isValid = true;
        }

        return isValid;
    }
};
const validatorNames = { "investment-amount": "investmentAmount" };

let clientWidth = document.documentElement.clientWidth;
let oldInvestmentAmount = formatter.parseNumber($("#investment-growth-investment-amount").val());
let collapsePanelOnSticky = false;

function resetForms() {
    $("#asset-input-values").trigger("reset");
}

function setUpNumericInputs() {
    const elements = document.getElementsByClassName("numeric");

    for (let i = 0; i < elements.length; i++) {
        RMNumberInput(elements[i], { inputmode: "numeric" });
    }

    $(".numeric").on("focus", function () {
        let preVal = $(this).val();
        if (lang === "fr") {
            preVal = preVal.replace(/,/g, ".");
        }

        if (isNaN(preVal)) {
            if (lang === "fr") {
                preVal = preVal.replace(/\./g, ",");
            }

            let value = formatter.parseNumber(preVal).toString();

            if (lang === "fr") {
                value = value.replace(/\./g, ",");
            }

            $(this).val(value).trigger("select");
        } else {
            $(this).trigger("select");
        }
    });

    $(".numeric").on("keyup", ({ target }) => {
        const input = $(target);

        input.val(input.val().replace(/\D/g, ""));
    });
}

function fillFieldsOnBlur() {
    $("input").on("blur", ({ target }) => {
        const { id, type } = target;
        const values = getInputValues();

        for (const value in values) {
            if (!values[value]) {
                values[value] = 0;
            }
        }

        if (type === "text" && id.indexOf("investment-amount")) {
            const formattedNumber = $(target).val() ? formatter.formatNumber($(target).val()) : $(target).val();
            $(target).val(formattedNumber);
        }
    });
}

function setUpInputs() {
    setUpNumericInputs();
    fillFieldsOnBlur();

    const value = formatter.formatNumber(formatter.parseNumber(allTextInputs.eq(0).val()));
    allTextInputs.val(value);

    const blurKeyupEventDebounced = debounce(() => {
        clearGlobalsForRedraw(false, true);
        updateTiles();
    }, 400);

    allTextInputs.on("blur keyup", (event) => {
        const {
            target: { id },
            type
        } = event;

        let currentInvestmentAmount = 0;
        if (id === "investment-growth-investment-amount") {
            currentInvestmentAmount = formatter.parseNumber($("#investment-growth-investment-amount").val());
            const formattedValue = currentInvestmentAmount
                ? formatter.formatNumber(currentInvestmentAmount)
                : currentInvestmentAmount;
            $("#best-and-worst-investment-amount").val(formattedValue);
            $("#time-to-recover-investment-amount").val(formattedValue);
        } else if (id === "best-and-worst-investment-amount") {
            currentInvestmentAmount = formatter.parseNumber($("#best-and-worst-investment-amount").val());
            const formattedValue = currentInvestmentAmount
                ? formatter.formatNumber(currentInvestmentAmount)
                : currentInvestmentAmount;
            $("#investment-growth-investment-amount").val(formattedValue);
            $("#time-to-recover-investment-amount").val(formattedValue);
        } else {
            currentInvestmentAmount = formatter.parseNumber($("#time-to-recover-investment-amount").val());
            const formattedValue = currentInvestmentAmount
                ? formatter.formatNumber(currentInvestmentAmount)
                : currentInvestmentAmount;
            $("#investment-growth-investment-amount").val(formattedValue);
            $("#best-and-worst-investment-amount").val(formattedValue);
        }

        const validatorName = validatorNames["investment-amount"];
        validators[validatorName](type);

        if (currentInvestmentAmount !== oldInvestmentAmount && areInputsValid(type)) {
            oldInvestmentAmount = currentInvestmentAmount;
            blurKeyupEventDebounced();
        }
    });

    const dropDownDebounced = (tile) =>
        debounce(() => {
            clearGlobalsForRedraw(false, true, [tile]);
            updateTiles([tile]);
        }, 400);

    bestAndWorstTimePeriodDropdown.on("change", ({ type }) => {
        if (areInputsValid(type)) {
            dropDownDebounced("bestAndWorst")();
        }
    });

    timeToRecoverTimePeriodDropdown.on("change", ({ type }) => {
        if (areInputsValid(type)) {
            dropDownDebounced("timeToRecover")();
        }
    });

    $("input, select").on("focus keyup blur", () => $("[class^='popup']").css("visibility", "hidden"));
}

function setUpButtons() {
    pdfReportButton.on("click", (event) => {
        const { type } = event;

        if (areInputsValid(type)) {
            populatePDF();
            generatePDF();
        } else {
            event.preventDefault();
        }
    });

    printReportButton.on("click", (event) => {
        const { type } = event;

        if (!areInputsValid(type)) {
            event.preventDefault();
        } else {
            window.open("print.html")
        }
    });
}

function areInputsValid(eventType) {
    let isValid = true;

    Object.keys(validators).forEach((inputName) => {
        if (!validators[inputName](eventType)) {
            isValid = false;
        }
    });

    return isValid;
}

function getInputValues() {
    const assetMixes = $("#asset-mix-checkboxes input:checked")
        .map(function () {
            return $("#asset-mix-checkboxes input").index(this);
        })
        .get();
    const investmentAmount = oldInvestmentAmount;
    const bestAndWorstTimePeriod = formatter.parseNumber(getValueFromInput("#best-worst-time-period"));
    const timeToRecoverTimePeriod = formatter.parseNumber(getValueFromInput("#time-to-recover-time-period"));

    return {
        assetMixes,
        investmentAmount,
        timePeriods: {
            bestAndWorst: bestAndWorstTimePeriod,
            timeToRecover: timeToRecoverTimePeriod
        }
    };
}

function setUpAssetMixCheckboxes() {
    const allCheckboxes = $("#asset-mix-checkboxes input");

    $("#asset-mix-checkboxes input:not(:checked)").prop("disabled", true);

    const checkboxesDebounced = debounce(() => {
        updateAssetMixPiesAndTexts();
        clearGlobalsForRedraw();
        updateTiles();
    }, 400);

    allCheckboxes.on("change", function () {
        const checkedCheckboxes = $("#asset-mix-checkboxes input:checked");
        const uncheckedCheckboxes = $("#asset-mix-checkboxes input:not(:checked)");

        if (checkedCheckboxes.length < 1) {
            $(this).prop("checked", true);
        } else if (checkedCheckboxes.length > 2) {
            $(this).prop("checked", false);
        } else if (checkedCheckboxes.length === 2) {
            uncheckedCheckboxes.prop("disabled", true);
            checkboxesDebounced();
        } else if (checkedCheckboxes.length === 1) {
            uncheckedCheckboxes.prop("disabled", false);
            checkboxesDebounced();
        }
    });
}

const handleScrollThrottle = throttle(
    function () {
        const assetInputValues = $("#asset-input-values");
        if ($(this).scrollTop() + 55 < $(".asset-input-container").position().top) {
            assetInputValues.removeClass("collapsed").addClass("expanded");
            $(".arrow-asset-container").removeClass("show");
        } else {
            if (collapsePanelOnSticky) {
                assetInputValues.removeClass("expanded").addClass("collapsed");
            } else {
                assetInputValues.removeClass("collapsed").addClass("expanded");
            }

            $(".arrow-asset-container").addClass("show");
        }
    },
    250,
    true
);

function setUpAssetMixInputsPanel() {
    if (
        window.CSS &&
        window.CSS.supports &&
        (window.CSS.supports("position", "sticky") || window.CSS.supports("position", "-webkit-sticky")) &&
        clientWidth >= 768
    ) {
        $(document).on("scroll", handleScrollThrottle).trigger("scroll");
    }

    $(".arrow-asset").on("click", ({ target }) => {
        const panel = $(target).closest(".card-content");

        if (panel.hasClass("collapsed")) {
            collapsePanelOnSticky = false;
            panel.removeClass("collapsed").addClass("expanded");
        } else {
            collapsePanelOnSticky = true;
            panel.removeClass("expanded").addClass("collapsed");
        }
    });
}

function updateAssetMixPiesAndTexts() {
    const { assetMixes } = getInputValues();

    for (let i = 0; i < assetMixes.length; i++) {
        const assetMix = assetMixes[i];
        const {
            colours: { dark },
            image,
            label
        } = assetMixesInfo[assetMix];
        const [rValue, gValue, bValue] = getRGBFromHex(dark);
        const assetMixID = i === 0 ? "asset-mix-one" : "asset-mix-two";
        const assetMixDataID = i === 0 ? "assetMixOne" : "assetMixTwo";
        const assetMixColourClass = i === 0 ? "circle-light" : "circle-dark";
        const assetMixCanvasAriaLabel = 
            lang === "en" 
                ? i === 0 
                    ? "Pie chart showing positive and negative 1-year returns for Asset Mix One - " 
                    : "Pie chart showing positive and negative 1-year returns for Asset Mix Two - "
                : i === 0
                    ? "Graphique circulaire des rendements sur 1 an positifs de la première répartition de l’actif"
                    : "Graphique circulaire des rendements sur 1 an positifs de la deuxième répartition de l’actif"

        $(`.${assetMixID}-pie`).attr({
            src: `./common/images/asset-mix-pies/${image}`,
            alt: label[lang]
        });

        $(`[data-id='${assetMixDataID}Label']`).text(label[lang]);
        $(`[data-id='${assetMixDataID}ShortLabel']`).text(label[lang].replace(/[^\d/]/g, ""));
        $(`.legend .${assetMixColourClass}`).css("background-color", `rgb(${rValue},${gValue},${bValue})`);
        $(`[data-id='${assetMixDataID}Canvas']`).attr("aria-label", assetMixCanvasAriaLabel.concat(label[lang]));
    }

    const assetMixTwoDataID = "assetMixTwo";
    const assetMixTwoBestAndWorstLegend = $(`[data-id='${assetMixTwoDataID}BestAndWorstLegend']`);
    const assetMixTwoLabels = $(`[data-id='${assetMixTwoDataID}Label']`);
    const assetMixTwoShortLabel = $(`[data-id='${assetMixTwoDataID}ShortLabel']`);
    const assetMixBestAndWorstLabels = $(".best-worst-label");
    if (assetMixes.length < 2) {
        assetMixBestAndWorstLabels.css("width", "100%");
        assetMixTwoBestAndWorstLegend.hide();
        assetMixTwoLabels.closest(".best-worst-label, .item:not(.pie-section), .legend, .performance-container").hide();
        assetMixTwoShortLabel.closest(".img-container").hide();
    } else {
        assetMixBestAndWorstLabels.css("width", "50%");
        assetMixTwoBestAndWorstLegend.show();
        assetMixTwoLabels.closest(".best-worst-label, .item:not(.pie-section), .legend, .performance-container").show();
        assetMixTwoShortLabel.closest(".img-container").show();
    }
}

function updateTiles(tiles = ["investmentGrowth", "bestAndWorst", "timeToRecover"]) {
    const { assetMixes, investmentAmount, timePeriods } = getInputValues();

    calculateResults({ assetMixes, investmentAmount, timePeriods }).then((results) => {
        const { investmentGrowth, bestAndWorst, timeToRecover } = results;

        if (tiles.indexOf("investmentGrowth") > -1) {
            updateInvestmentGrowthTile(investmentGrowth);
        }

        if (tiles.indexOf("bestAndWorst") > -1) {
            updateBestAndWorstTile(bestAndWorst);
        }

        if (tiles.indexOf("timeToRecover") > -1) {
            updateTimeToRecoverTile(timeToRecover);
        }

        updatePrintValues(results);
    });
}

function removeAccessibilityTables(options) {
    const removeTable = document.getElementById(options.title + "-table-content");
    if (removeTable) {
        const tableParent = removeTable.parentElement;
        tableParent.innerHTML = "";
    }
}

function updateInvestmentGrowthTile(results) {
    const firstAssetMixName = document.querySelector(".best-worst-label").innerText;
    function drawCallback({ isHovered, last, lineColour, portfolioValue, screenPixelRatio }) {
        return (context, x, y) => {
            if (last) {
                context.save();

                const { assetMixOne, assetMixTwo } = results;
                const fontSize = 15;
                let yOffset = (fontSize + 5) * screenPixelRatio;

                if (assetMixTwo) {
                    const { portfolioValue: assetMixOnePortfolioValue } = assetMixOne.slice(-1)[0];
                    const { portfolioValue: assetMixTwoPortfolioValue } = assetMixTwo.slice(-1)[0];
                    const minPortfolioValue = Math.min(assetMixOnePortfolioValue, assetMixTwoPortfolioValue);

                    if (portfolioValue <= minPortfolioValue) {
                        yOffset = (fontSize * 2 + 10) * screenPixelRatio;
                    }
                }

                context.textAlign = "end";
                context.textBaseline = "bottom";
                context.font = `${isHovered ? "bold " : ""}${fontSize * screenPixelRatio}px Roobert`;

                const portfolioValueText = formatter.formatCurrency(portfolioValue, 0);
                const textWidth = getTextWidth(portfolioValueText, context.font);
                y = yOffset;

                const path = new Path2D();
                context.fillStyle = "white";
                context.strokeStyle = "transparent";
                if (!isIE()) {
                    path.rect(x - textWidth, y - fontSize * screenPixelRatio, textWidth, fontSize * screenPixelRatio);
                } else {
                    path.moveTo(x - textWidth, y - fontSize * screenPixelRatio);
                    path.lineTo(x, y - fontSize * screenPixelRatio);
                    path.lineTo(x, y);
                    path.lineTo(x - textWidth, y);
                }
                context.fill(path);
                InvestmentGrowthGraphGlobal.legendPaths[lineColour] = path;

                context.fillStyle = "#000000";
                context.fillText(portfolioValueText, x, y);

                context.fillStyle = lineColour;
                const radius = 5;
                x = x - textWidth - radius - 10 * screenPixelRatio;
                y = yOffset - (fontSize / 2) * screenPixelRatio;

                context.beginPath();
                context.arc(x, y, radius * screenPixelRatio, 0, 2 * Math.PI);
                context.fill();

                context.restore();
            }
        };
    }

    InvestmentGrowthGraphGlobal.drawCallback = drawCallback;

    function drawTable({ accessibilityTableDiv, options: { title } }) {
        function getIndices(data) {
            const datesForDashedLines = ["06/1990", "06/2000", "06/2010"];
            const pointsIndices = showPoints("page")(data);

            return data.reduce(
                (result, { date }, index) =>
                    datesForDashedLines.indexOf(date) > -1 || pointsIndices.indexOf(index) > -1
                        ? result.concat(index)
                        : result,
                []
            );
        }
        const { assetMixOne, assetMixTwo } = results;
        const { assetMixes } = getInputValues();
        const dataLabels = {
            en: {
                tenYears: "10 years",
                twentyYears: "20 years",
                thirtyYears: "30 years",
                bestYear: "Best 1 year period",
                worstYear: "Worst 1 year period"
            },
            fr: {
                tenYears: "10 ans",
                twentyYears: "20 ans",
                thirtyYears: "30 ans",
                bestYear: "Meilleure période de 1 an",
                worstYear: "Pire période de 1 an"
            }
        };

        let indices = getIndices(assetMixOne);
        let dataPoints = assetMixOne.filter((_value, index) => indices.indexOf(index) > -1);
        let dashedLinesDataPoints = dataPoints.filter(({ bestYear, worstYear }) => !bestYear && !worstYear);
        let [bestYearDataPoint] = dataPoints.filter(({ bestYear }) => bestYear);
        let [worstYearDataPoint] = dataPoints.filter(({ worstYear }) => worstYear);
        let tableData = [["", assetMixesInfo[assetMixes[0]].label[lang]]];
        dashedLinesDataPoints.forEach(({ date, portfolioValue }) => {
            let dataLabel = dataLabels[lang].tenYears;
            if (date === "06/2000") {
                dataLabel = dataLabels[lang].twentyYears;
            } else if (date === "06/2010") {
                dataLabel = dataLabels[lang].thirtyYears;
            }
            tableData = tableData.concat([[dataLabel, formatter.formatCurrency(portfolioValue, 0)]]);
        });
        tableData = tableData.concat([
            [dataLabels[lang].bestYear, formatter.formatPercentage(bestYearDataPoint.rateOfReturn * 100, 1)]
        ]);
        tableData = tableData.concat([
            [dataLabels[lang].worstYear, formatter.formatPercentage(worstYearDataPoint.rateOfReturn * 100, 1)]
        ]);

        if (assetMixTwo) {
            indices = getIndices(assetMixTwo);
            dataPoints = assetMixTwo.filter((_value, index) => indices.indexOf(index) > -1);
            dashedLinesDataPoints = dataPoints.filter(({ bestYear, worstYear }) => !bestYear && !worstYear);
            [bestYearDataPoint] = dataPoints.filter(({ bestYear }) => bestYear);
            [worstYearDataPoint] = dataPoints.filter(({ worstYear }) => worstYear);
            tableData[0] = tableData[0].concat(assetMixesInfo[assetMixes[1]].label[lang]);
            dashedLinesDataPoints.forEach(({ portfolioValue }, index) => {
                tableData[index + 1] = tableData[index + 1].concat(formatter.formatCurrency(portfolioValue, 0));
            });
            const lastIndex = tableData.length - 1;
            tableData[lastIndex - 1] = tableData[lastIndex - 1].concat(
                formatter.formatPercentage(bestYearDataPoint.rateOfReturn * 100, 1)
            );
            tableData[lastIndex] = tableData[lastIndex].concat(
                formatter.formatPercentage(worstYearDataPoint.rateOfReturn * 100, 1)
            );
        }

        const table = document.createElement("table");
        table.setAttribute("id", `${title}-table-content`);
        let tr = undefined;
        let th = undefined;
        let td = undefined;
        for (let i = 0; i < tableData.length; i++) {
            const rowData = tableData[i];
            tr = document.createElement("tr");
            table.appendChild(tr);
            for (let j = 0; j < rowData.length; j++) {
                if (i === 0) {
                    th = document.createElement("th");
                    th.setAttribute("scope", "col");
                    if (j === 0) {
                        // th.setAttribute("aria-hidden", "true");
                        th.appendChild(document.createTextNode("Number of years"));
                    }
                    th.appendChild(document.createTextNode(rowData[j]));
                    tr.appendChild(th);
                } else {
                    if (j === 0) {
                        th = document.createElement("th");
                        th.setAttribute("scope", "row");
                        th.appendChild(document.createTextNode(rowData[j]));
                        tr.appendChild(th);
                    } else {
                        td = document.createElement("td");
                        td.appendChild(document.createTextNode(rowData[j]));
                        tr.appendChild(td);
                    }
                }
            }
        }
        accessibilityTableDiv.appendChild(table);
    }

    function showPoints(type) {
        return (data) => {
            let indices = [];

            if (type === "page") {
                indices = data.reduce(
                    (result, { bestYear, worstYear }, index) => (bestYear || worstYear ? result.concat(index) : result),
                    indices
                );
            }

            return indices;
        };
    }

    function updateContent({ dataName, dataName2, popup, x, x2 }) {
        const { assetMixOne, assetMixTwo } = results;
        const titles = {
            bestYear: {
                en: "Best 1 year period",
                fr: "Meilleure période de 1 an"
            },
            worstYear: {
                en: "Worst 1 year period",
                fr: "Pire période de 1 an"
            }
        };
        const dataset = InvestmentGrowthGraphGlobal.dataset;

        if ($(popup).hasClass("popup-stacked")) {
            let xVal = x;
            let datasetName = dataName;

            const biggestDatasetName = getBiggestDatasetName();

            if (biggestDatasetName !== dataName) {
                xVal = x2;
                datasetName = dataName2;
            }

            let assetMix = assetMixOne;
            if (datasetName !== firstAssetMixName) {
                assetMix = assetMixTwo;
            }

            let { bestYear, rateOfReturn } = assetMix.filter(({ date }) => getTimestampFromDate(date) === xVal)[0];
            let title = bestYear ? titles.bestYear[lang] : titles.worstYear[lang];
            let value = formatter.formatPercentage(rateOfReturn * 100, 1);
            let colour = dataset[0].name === datasetName ? dataset[0].css.colour : dataset[1].css.colour;

            $(popup).find("[data-id='titleOne']").text(title);
            $(popup).find(".legend-one .circle").css("background-color", colour);
            $(popup).find("[data-id='assetMixOneValue']").text(value);

            if (datasetName === dataName) {
                datasetName = dataName2;
                xVal = x2;
            } else {
                datasetName = dataName;
                xVal = x;
            }

            if (datasetName !== firstAssetMixName) {
                assetMix = assetMixTwo;
            } else {
                assetMix = assetMixOne;
            }

            let { bestYear: bestYear2, rateOfReturn: rateOfReturn2 } = assetMix.filter(
                ({ date }) => getTimestampFromDate(date) === xVal
            )[0];
            title = bestYear2 ? titles.bestYear[lang] : titles.worstYear[lang];
            value = formatter.formatPercentage(rateOfReturn2 * 100, 1);
            colour = dataset[0].name === datasetName ? dataset[0].css.colour : dataset[1].css.colour;

            if ($(popup).find("[data-id='titleOne']").text() === title) {
                $(popup).find("[data-id='titleSecond']").hide();
            } else {
                $(popup).find("[data-id='titleSecond']").show();
                $(popup).find("[data-id='titleSecond']").text(title);
            }

            $(popup).find(".legend-two .circle").css("background-color", colour);
            $(popup).find("[data-id='assetMixTwoValue']").text(value);
        } else {
            let assetMix = assetMixOne;
            if (dataName !== firstAssetMixName) {
                assetMix = assetMixTwo;
            }

            const { bestYear, rateOfReturn } = assetMix.filter(({ date }) => getTimestampFromDate(date) === x)[0];

            const title = bestYear ? titles.bestYear[lang] : titles.worstYear[lang];
            const value = formatter.formatPercentage(rateOfReturn * 100, 1);
            const colour = dataset[0].name === dataName ? dataset[0].css.colour : dataset[1].css.colour;
            $(popup).find("[data-id='title']").text(title);
            $(popup).find(".legend .circle").css("background-color", colour);
            $(popup).find("[data-id='value']").text(value);
        }
    }

    function getBiggestDatasetName() {
        const dataset = InvestmentGrowthGraphGlobal.dataset;

        const mixOneLastValue = dataset[0].points[dataset[0].points.length - 1].y;
        const mixTwoLastValue = dataset[1].points[dataset[1].points.length - 1].y;

        if (mixOneLastValue > mixTwoLastValue) {
            return dataset[0].name;
        }

        return dataset[1].name;
    }

    function drawGraphForType({ canvas, type }) {
        let screenPixelRatio = devicePixelRatio;
        let screenWidth = $(window).width();
        if (type === "page") {
            if (screenWidth < 576) {
                screenWidth = "small";
            } else if (screenWidth < 768) {
                screenWidth = "medium";
            } else {
                screenWidth = "regular";
            }

            canvas.style.width = "100%";
        } else {
            screenPixelRatio = 1;
            screenWidth = "regular";
        }

        const context = canvas.getContext("2d");

        const css = getInvestmentGrowthGraphCSS({ screenPixelRatio, screenWidth });
        if (type === "page") {
            InvestmentGrowthGraphGlobal.css = css;
        }

        const lineColourOne = $("#investment-growth-panel .circle-light").css("background-color");
        const lineColourTwo = $("#investment-growth-panel .circle-dark").css("background-color");
        const dataset =
            InvestmentGrowthGraphGlobal.dataset && type === "page"
                ? InvestmentGrowthGraphGlobal.dataset
                : getInvestmentGrowthGraphDataset({
                      ...results,
                      canvas,
                      drawCallback,
                      lineColourOne,
                      lineColourTwo,
                      page: type === "page",
                      screenPixelRatio,
                      showPoints: showPoints(type)
                  });

        if (!InvestmentGrowthGraphGlobal.dataset && type === "page") {
            InvestmentGrowthGraphGlobal.dataset = dataset;
        }

        const popupOne = investmentGrowthGraph.closest(".graph-container").find(".popup-one");
        const popupDashedLine = investmentGrowthGraph.closest(".graph-container").find(".popup-dashed-line");
        const popupStacked = investmentGrowthGraph.closest(".graph-container").find(".popup-stacked");
        const options =
            InvestmentGrowthGraphGlobal.options && type === "page"
                ? InvestmentGrowthGraphGlobal.options
                : getInvestmentGrowthGraphOptions({
                      ...results,
                      canvas,
                      css,
                      screenPixelRatio,
                      screenWidth,
                      updateContent,
                      popupOne,
                      popupDashedLine,
                      popupStacked
                  });

        if (!InvestmentGrowthGraphGlobal.options && type === "page") {
            InvestmentGrowthGraphGlobal.options = options;
        }

        if (!InvestmentGrowthGraphGlobal.graphAnimator) {
            InvestmentGrowthGraphGlobal.graphAnimator = createGraphAnimator(
                {
                    animSpeed: 250,
                    leftToRightAnimation: true
                },
                canvas
            );
        }

        context.clearRect(0, 0, canvas.width, canvas.height);
        if (type === "page") {
            if (isIE()) {
                const pageCanvasTop = document.getElementById("investment-growth-graph-top");
                const pageCanvasMiddle = document.getElementById("investment-growth-graph-middle");

                pageCanvasTop.height = pageCanvasMiddle.height = canvas.height;
                pageCanvasTop.width = pageCanvasMiddle.width = canvas.width;

                const transparentLines = [];
                $.extend(true, transparentLines, dataset);

                if (dataset[1].type === "line") {
                    transparentLines
                        .filter(
                            (set) =>
                                set.type !== "dashedLine" &&
                                InvestmentGrowthGraphGlobal.previousColours.indexOf(set.css.colour) === -1
                        )
                        .forEach((set) => {
                            set.css.colour = "transparent";
                            set.css.points.showPoints = [];
                        });
                    options.accessibilityTable = false;
                    drawGraph(canvas, options, css, transparentLines);
                    drawGraph(pageCanvasTop, options, css, dataset);

                    pageCanvasTop.style.clip =
                        pageCanvasMiddle.style.clip = `rect(0px, 0px, ${pageCanvasTop.height}px, 0px)`;
                    pageCanvasTop.classList.remove("runAnimation");
                    pageCanvasMiddle.classList.remove("runAnimation");
                    pageCanvasTop.style.display = pageCanvasMiddle.style.display = "block";

                    setTimeout(() => {
                        pageCanvasTop.classList.add("runAnimation");
                        pageCanvasMiddle.classList.add("runAnimation");
                    }, 250);

                    pageCanvasTop.addEventListener("animationstart", () => {
                        pageCanvasTop.style.clip =
                            pageCanvasMiddle.style.clip = `rect(0px, ${pageCanvasTop.width}px, ${pageCanvasTop.height}px, 0px)`;
                        InvestmentGrowthGraphGlobal.enableHover = false;
                    });

                    pageCanvasTop.addEventListener("animationend", () => {
                        pageCanvasTop.style.clip =
                            pageCanvasMiddle.style.clip = `rect(0px, ${pageCanvasTop.width}px, ${pageCanvasTop.height}px, 0px)`;
                        pageCanvasTop.style.display = pageCanvasMiddle.style.display = "none";

                        pageCanvas.getContext("2d").clearRect(0, 0, pageCanvasTop.width, pageCanvasTop.height);

                        InvestmentGrowthGraphGlobal.enableHover = true;
                        options.accessibilityTable = false;
                        drawGraph(canvas, options, css, dataset);
                    });
                } else {
                    drawGraph(canvas, options, css, dataset);
                }
            } else if (
                InvestmentGrowthGraphGlobal.initialDraw ||
                InvestmentGrowthGraphGlobal.initialDraw === undefined
            ) {
                InvestmentGrowthGraphGlobal.enableHover = true;
                InvestmentGrowthGraphGlobal.graphAnimator = createGraphAnimator(
                    {
                        animSpeed: 3,
                        leftToRightAnimation: true
                    },
                    canvas
                );
                setTimeout(() => {
                    InvestmentGrowthGraphGlobal.graphAnimator(
                        options,
                        css,
                        InvestmentGrowthGraphGlobal.oldDataset,
                        dataset
                    );
                    drawGraph(canvas, options, css, dataset);
                    InvestmentGrowthGraphGlobal.initialDraw = false;
                    InvestmentGrowthGraphGlobal.initialTimeout = 0;
                    InvestmentGrowthGraphGlobal.reanimateAllLines = false;
                    InvestmentGrowthGraphGlobal.graphAnimator = createGraphAnimator(
                        {
                            animSpeed: 250
                        },
                        canvas
                    );
                }, InvestmentGrowthGraphGlobal.initialTimeout);
            } else {
                InvestmentGrowthGraphGlobal.enableHover = true;
                drawAnimatedGraph(
                    context,
                    InvestmentGrowthGraphGlobal.graphAnimator,
                    options,
                    css,
                    dataset,
                    dataset,
                    InvestmentGrowthGraphGlobal,
                    true
                );
                drawGraph(canvas, options, css, dataset);
            }

            InvestmentGrowthGraphGlobal.previousColours = [lineColourOne];
            if (results.assetMixTwo) {
                InvestmentGrowthGraphGlobal.previousColours.push(lineColourTwo);
            }
        } else {
            drawGraph(canvas, options, css, dataset);
        }

        if (type === "page") {
            const titles = {
                en: "Investment Growth",
                fr: "Croissance des placements"
            };
            const accessibilityTableOptions = { title: titles[lang].split(" ").join("-").toLowerCase() };
            removeAccessibilityTables(accessibilityTableOptions);
            drawTable({ accessibilityTableDiv: investmentGrowthGraphTable, options: accessibilityTableOptions });
        }
    }

    const pageCanvas = investmentGrowthGraph.get(0);
    pageCanvas.height = 420 * devicePixelRatio;
    pageCanvas.width = investmentGrowthGraph.closest(".graph-container").outerWidth() * devicePixelRatio;
    drawGraphForType({ canvas: pageCanvas, type: "page" });

    const printCanvas = investmentGrowthGraphPrint.get(0);
    printCanvas.height = 420;
    printCanvas.width = 863;
    drawGraphForType({ canvas: printCanvas, type: "print" });
}

function updateBestAndWorstTile(results) {
    function drawBestAndWorstGraph() {
        function drawCallback({ assetMix, isHovered, screenPixelRatio, screenWidth, yearType }) {
            return (context, x, y, w) => {
                context.save();

                const { portfolioValue } = assetMix[`${yearType}Year`];
                const value = formatter.formatCurrency(portfolioValue, 0);
                let fontSize = 16;
                if (screenWidth === "extraSmall") {
                    fontSize = 12;
                } else if (screenWidth === "small") {
                    fontSize = 14;
                }

                context.fillStyle = "#333333";
                context.font = `bold ${fontSize * screenPixelRatio}px Roobert`;
                context.shadowBlur = 0;
                context.shadowOffsetX = 0;
                context.shadowOffsetY = 0;
                context.textAlign = "center";
                context.textBaseline = "bottom";

                const { investmentAmount } = getInputValues();
                const textWidth = getTextWidth(value, context.font);
                if (yearType === "worst" && portfolioValue >= investmentAmount && textWidth / 2 >= w / 2) {
                    x += textWidth / 2 + 5;
                } else {
                    x += w / 2;
                }
                y = portfolioValue >= investmentAmount ? y - 5 * screenPixelRatio : y + 25 * screenPixelRatio;

                if (isHovered) {
                    fontSize += 3;
                    y = portfolioValue >= investmentAmount ? y - 5 * screenPixelRatio : y + 5 * screenPixelRatio;
                }

                context.fillText(value, x, y);

                context.restore();
            };
        }

        BestAndWorstGraphGlobal.drawCallback = drawCallback;

        function drawTable({ accessibilityTableDiv, options: { title } }) {
            const { assetMixOne, assetMixTwo } = results;
            const {
                assetMixes,
                timePeriods: { bestAndWorst }
            } = getInputValues();
            const dataLabels = {
                en: {
                    bestPeriod: `Best ${bestAndWorst} year period`,
                    bestValue: `Best ${bestAndWorst} year value`,
                    worstPeriod: `Worst ${bestAndWorst} year period`,
                    worstValue: `Worst ${bestAndWorst} year value`
                },
                fr: {
                    bestPeriod: `Meilleure période de ${pluralizeText("an", bestAndWorst)}`,
                    bestValue: `Meilleure valeur sur ${pluralizeText("an", bestAndWorst)}`,
                    worstPeriod: `Pire période de  ${pluralizeText("an", bestAndWorst)}`,
                    worstValue: `Pire valeur sur ${pluralizeText("an", bestAndWorst)}`
                }
            };

            let tableData = [
                ["", assetMixesInfo[assetMixes[0]].label[lang]],
                [
                    dataLabels[lang].bestPeriod,
                    `${getDateRangeFromDate(assetMixOne.bestYear.date)} (${formatter.formatPercentage(
                        assetMixOne.bestYear.rateOfReturn * 100,
                        1
                    )})`
                ],
                [dataLabels[lang].bestValue, formatter.formatCurrency(assetMixOne.bestYear.portfolioValue, 0)],
                [
                    dataLabels[lang].worstPeriod,
                    `${getDateRangeFromDate(assetMixOne.worstYear.date)} (${formatter.formatPercentage(
                        assetMixOne.worstYear.rateOfReturn * 100,
                        1
                    )})`
                ],
                [dataLabels[lang].worstValue, formatter.formatCurrency(assetMixOne.worstYear.portfolioValue, 0)]
            ];

            if (assetMixTwo) {
                tableData[0] = tableData[0].concat(assetMixesInfo[assetMixes[1]].label[lang]);
                tableData[1] = tableData[1].concat(
                    `${getDateRangeFromDate(assetMixTwo.bestYear.date)} (${formatter.formatPercentage(
                        assetMixTwo.bestYear.rateOfReturn * 100,
                        1
                    )})`
                );
                tableData[2] = tableData[2].concat(formatter.formatCurrency(assetMixTwo.bestYear.portfolioValue, 0));
                tableData[3] = tableData[3].concat(
                    `${getDateRangeFromDate(assetMixTwo.worstYear.date)} (${formatter.formatPercentage(
                        assetMixTwo.worstYear.rateOfReturn * 100,
                        1
                    )})`
                );
                tableData[4] = tableData[4].concat(formatter.formatCurrency(assetMixTwo.worstYear.portfolioValue, 0));
            }

            const table = document.createElement("table");
            table.setAttribute("id", `${title}-table-content`);
            let tr = undefined;
            let th = undefined;
            let td = undefined;
            for (let i = 0; i < tableData.length; i++) {
                const rowData = tableData[i];
                tr = document.createElement("tr");
                table.appendChild(tr);
                for (let j = 0; j < rowData.length; j++) {
                    if (i === 0) {
                        th = document.createElement("th");
                        th.setAttribute("scope", "col");
                        if (j === 0) {
                            th.appendChild(document.createTextNode(lang === "en" ? "Investment period and value" : "Période de placement et la valeur"));
                        }
                        th.appendChild(document.createTextNode(rowData[j]));
                        tr.appendChild(th);
                    } else {
                        if (j === 0) {
                            th = document.createElement("th");
                            th.setAttribute("scope", "row");
                            th.appendChild(document.createTextNode(rowData[j]));
                            tr.appendChild(th);
                        } else {
                            td = document.createElement("td");
                            td.appendChild(document.createTextNode(rowData[j]));
                            tr.appendChild(td);
                        }
                    }
                }
            }
            accessibilityTableDiv.appendChild(table);
        }

        function getDateRangeFromDate(date) {
            const { timePeriods } = getInputValues();
            let [month, year] = date.split("/");
            month = Number(month);
            year = Number(year);
            let endDate = new Date(`${month}/01/${year}`);
            const startDateMonth = month - 1 < 1 ? 12 : month - 1;
            let startDate = new Date(`${startDateMonth}/01/${year - timePeriods.bestAndWorst}`);
            endDate = formatter
                .formatDate(endDate, "mmm yyyy")
                .replace(/June/g, "Jun")
                .replace(/July/g, "Jul")
                .replace(/1éc/g, "déc");
            startDate = formatter
                .formatDate(startDate, "mmm yyyy")
                .replace(/June/g, "Jun")
                .replace(/July/g, "Jul")
                .replace(/1éc/g, "déc");

            return `${startDate} - ${endDate}`;
        }

        function updateContent({ dataName, popup, x }) {
            const { assetMixOne, assetMixTwo } = results;
            let assetMix = assetMixOne;
            if (x > 4) {
                assetMix = assetMixTwo;
            }

            const title = dataName.split(" - ")[1];
            const searchString = lang === "en" ? "Best" : "Meilleure";
            const { bestYear, worstYear } = assetMix;
            const { date, rateOfReturn } = dataName.indexOf(searchString) > -1 ? bestYear : worstYear;
            const dateRange = getDateRangeFromDate(date);
            const formattedRateOfReturn = formatter.formatPercentage(rateOfReturn * 100, 1);
            const value = `${dateRange} (${formattedRateOfReturn})`;

            $(popup).find("[data-id='title']").text(title);
            $(popup).find("[data-id='value']").text(value);
        }

        function drawGraphForType({ assetMix, canvas, type }) {
            let screenPixelRatio = devicePixelRatio;
            let screenWidth = $(window).outerWidth();
            if (type === "page") {
                if (screenWidth < 321) {
                    screenWidth = "extraSmall";
                } else if (screenWidth < 576) {
                    screenWidth = "small";
                } else if (screenWidth < 1100) {
                    screenWidth = "mediumLarge";
                } else {
                    screenWidth = "regular";
                }
            } else {
                screenPixelRatio = 1;
                screenWidth = "regular";
            }

            const context = canvas.getContext("2d");

            const { assetMixes, investmentAmount, timePeriods } = getInputValues();
            const css = (BestAndWorstGraphGlobal.css = getBestAndWorstGraphCSS({
                investmentAmount,
                screenPixelRatio,
                screenWidth
            }));

            const barColourOne = assetMixesInfo[assetMixes[0]].colours.dark;
            const barColourTwo = assetMixesInfo[assetMixes[0]].colours.light;
            const barColourThree = assetMixes.length > 1 ? assetMixesInfo[assetMixes[1]].colours.dark : "#ffffff";
            const barColourFour = assetMixes.length > 1 ? assetMixesInfo[assetMixes[1]].colours.light : "#ffffff";

            let args = {
                ...results,
                barColourOne,
                barColourTwo,
                barColourThree,
                barColourFour,
                css,
                drawCallback,
                investmentAmount,
                screenPixelRatio,
                screenWidth,
                timePeriod: timePeriods.bestAndWorst
            };
            if (type === "print") {
                args = filterObjectByKeys(args, ["assetMixTwo"]);
                if (assetMix === 2) {
                    args.assetMixOne = results.assetMixTwo;
                    args.barColourOne = barColourThree;
                    args.barColourTwo = barColourFour;
                }
            }

            const barImageOne = new Image();
            const barImageTwo = new Image();
            const barImageThree = new Image();
            const barImageFour = new Image();

            function haveAllImagesLoaded(image) {
                return new Promise(resolve => {
                    image.onload = () => resolve();
                })
            };

            if ((barColourOne === "#333333" || barColourOne === "#6c6f70") && (barColourTwo === "#6c6f70" || barColourTwo === "#a7a9a9") && barColourThree === "#333333" && barColourFour === "#6c6f70") {
                Promise.all([barImageOne, barImageTwo, barImageThree, barImageFour].map((img) => {
                    return haveAllImagesLoaded(img);
                })).then(() => {
                    args.barImageOne = barImageOne;
                    args.barImageTwo = barImageTwo;
                    args.barImageThree = barImageThree;
                    args.barImageFour = barImageFour;
                    const dataset =
                    BestAndWorstGraphGlobal.dataset && type === "page"
                        ? BestAndWorstGraphGlobal.dataset
                        : getBestAndWorstGraphDataset(args);
    
                    if (!BestAndWorstGraphGlobal.dataset && type === "page") {
                        BestAndWorstGraphGlobal.dataset = dataset;
                    }
    
                    const popup = bestAndWorstGraph
                        .closest(".graph-container")
                        .find(".popup, .popup-left, .popup-reverse, .popup-right, .popup-top");
    
                    args = {
                        ...results,
                        canvas,
                        investmentAmount,
                        screenWidth,
                        popup,
                        screenPixelRatio,
                        showPopup: showPopup(
                            true,
                            dataset
                                .map((data) => data.points)
                                .reduce((previousPoints, currentPoints) => previousPoints.concat(currentPoints), [])
                        ),
                        type,
                        updateContent
                    };
    
                    if (type === "print") {
                        args.axesLimits = getBestAndWorstGraphAxesLimits({ ...results, investmentAmount, type });
                        args = filterObjectByKeys(args, ["assetMixTwo"]);
                        if (assetMix === 2) {
                            args.assetMixOne = results.assetMixTwo;
                        }
                    }
    
                    const options =
                        BestAndWorstGraphGlobal.options && type === "page"
                            ? BestAndWorstGraphGlobal.options
                            : getBestAndWorstGraphOptions(args);
    
                    if (!BestAndWorstGraphGlobal.options && type === "page") {
                        BestAndWorstGraphGlobal.options = options;
                    }
    
                    if (!BestAndWorstGraphGlobal.graphAnimator && type === "page") {
                        BestAndWorstGraphGlobal.graphAnimator = createGraphAnimator(
                            {
                                animSpeed: 350
                            },
                            canvas
                        );
                    }
    
                    canvas.style.width = "100%";
                    context.clearRect(0, 0, canvas.width, canvas.height);
    
                    if (type === "page") {
                        if (BestAndWorstGraphGlobal.initialDraw || BestAndWorstGraphGlobal.initialDraw === undefined) {
                            setTimeout(() => {
                                BestAndWorstGraphGlobal.graphAnimator(
                                    options,
                                    css,
                                    BestAndWorstGraphGlobal.oldDataset,
                                    dataset
                                );
                                removeAccessibilityTables(options);
                                drawGraph(canvas, options, css, dataset);
                                BestAndWorstGraphGlobal.initialDraw = false;
                                BestAndWorstGraphGlobal.initialTimeout = 0;
                            }, BestAndWorstGraphGlobal.initialTimeout);
                        } else {
                            drawAnimatedGraph(
                                context,
                                BestAndWorstGraphGlobal.graphAnimator,
                                options,
                                css,
                                dataset,
                                dataset,
                                BestAndWorstGraphGlobal,
                                true
                            );
                            removeAccessibilityTables(options);
                            drawGraph(canvas, options, css, dataset);
                        }
    
                        $(".best-worst-labels-container").css(
                            "margin-left",
                            css.canvasCss.padding.left / devicePixelRatio
                        );
                    } else {
                        removeAccessibilityTables(options);
                        drawGraph(canvas, options, css, dataset);
                    }
    
                    const titles = {
                        en: "Best and Worst Years",
                        fr: "Meilleures et pires années"
                    };
                    const accessibilityTableOptions = { title: titles[lang].split(" ").join("-").toLowerCase() };
                    removeAccessibilityTables(accessibilityTableOptions);
                    drawTable({ accessibilityTableDiv: bestAndWorstGraphTable, options: accessibilityTableOptions });
                })
                barImageTwo.src = `./common/images/asset-mix-pies/colour-patterns/grey-solid.png`;
                barImageOne.src = `./common/images/asset-mix-pies/colour-patterns/black-dotted-pattern.png`;
                barImageFour.src = `./common/images/asset-mix-pies/colour-patterns/grey-solid.png`;
                barImageThree.src = `./common/images/asset-mix-pies/colour-patterns/black-dotted-pattern.png`;
            } else {
                const dataset =
                BestAndWorstGraphGlobal.dataset && type === "page"
                    ? BestAndWorstGraphGlobal.dataset
                    : getBestAndWorstGraphDataset(args);

                if (!BestAndWorstGraphGlobal.dataset && type === "page") {
                    BestAndWorstGraphGlobal.dataset = dataset;
                }

                const popup = bestAndWorstGraph
                    .closest(".graph-container")
                    .find(".popup, .popup-left, .popup-reverse, .popup-right, .popup-top");

                args = {
                    ...results,
                    canvas,
                    investmentAmount,
                    screenWidth,
                    popup,
                    screenPixelRatio,
                    showPopup: showPopup(
                        true,
                        dataset
                            .map((data) => data.points)
                            .reduce((previousPoints, currentPoints) => previousPoints.concat(currentPoints), [])
                    ),
                    type,
                    updateContent
                };

                if (type === "print") {
                    args.axesLimits = getBestAndWorstGraphAxesLimits({ ...results, investmentAmount, type });
                    args = filterObjectByKeys(args, ["assetMixTwo"]);
                    if (assetMix === 2) {
                        args.assetMixOne = results.assetMixTwo;
                    }
                }

                const options =
                    BestAndWorstGraphGlobal.options && type === "page"
                        ? BestAndWorstGraphGlobal.options
                        : getBestAndWorstGraphOptions(args);

                if (!BestAndWorstGraphGlobal.options && type === "page") {
                    BestAndWorstGraphGlobal.options = options;
                }

                if (!BestAndWorstGraphGlobal.graphAnimator && type === "page") {
                    BestAndWorstGraphGlobal.graphAnimator = createGraphAnimator(
                        {
                            animSpeed: 350
                        },
                        canvas
                    );
                }

                canvas.style.width = "100%";
                context.clearRect(0, 0, canvas.width, canvas.height);

                if (type === "page") {
                    if (BestAndWorstGraphGlobal.initialDraw || BestAndWorstGraphGlobal.initialDraw === undefined) {
                        setTimeout(() => {
                            BestAndWorstGraphGlobal.graphAnimator(
                                options,
                                css,
                                BestAndWorstGraphGlobal.oldDataset,
                                dataset
                            );
                            removeAccessibilityTables(options);
                            drawGraph(canvas, options, css, dataset);
                            BestAndWorstGraphGlobal.initialDraw = false;
                            BestAndWorstGraphGlobal.initialTimeout = 0;
                        }, BestAndWorstGraphGlobal.initialTimeout);
                    } else {
                        drawAnimatedGraph(
                            context,
                            BestAndWorstGraphGlobal.graphAnimator,
                            options,
                            css,
                            dataset,
                            dataset,
                            BestAndWorstGraphGlobal,
                            true
                        );
                        removeAccessibilityTables(options);
                        drawGraph(canvas, options, css, dataset);
                    }

                    $(".best-worst-labels-container").css(
                        "margin-left",
                        css.canvasCss.padding.left / devicePixelRatio
                    );
                } else {
                    removeAccessibilityTables(options);
                    drawGraph(canvas, options, css, dataset);
                }

                const titles = {
                    en: "Best and Worst Years",
                    fr: "Meilleures et pires années"
                };
                const accessibilityTableOptions = { title: titles[lang].split(" ").join("-").toLowerCase() };
                removeAccessibilityTables(accessibilityTableOptions);
                drawTable({ accessibilityTableDiv: bestAndWorstGraphTable, options: accessibilityTableOptions });
            }
            // const dataset =
            //     BestAndWorstGraphGlobal.dataset && type === "page"
            //         ? BestAndWorstGraphGlobal.dataset
            //         : getBestAndWorstGraphDataset(args);

            // if (!BestAndWorstGraphGlobal.dataset && type === "page") {
            //     BestAndWorstGraphGlobal.dataset = dataset;
            // }

            // const popup = bestAndWorstGraph
            //     .closest(".graph-container")
            //     .find(".popup, .popup-left, .popup-reverse, .popup-right, .popup-top");

            // args = {
            //     ...results,
            //     canvas,
            //     investmentAmount,
            //     screenWidth,
            //     popup,
            //     screenPixelRatio,
            //     showPopup: showPopup(
            //         true,
            //         dataset
            //             .map((data) => data.points)
            //             .reduce((previousPoints, currentPoints) => previousPoints.concat(currentPoints), [])
            //     ),
            //     type,
            //     updateContent
            // };

            // if (type === "print") {
            //     args.axesLimits = getBestAndWorstGraphAxesLimits({ ...results, investmentAmount, type });
            //     args = filterObjectByKeys(args, ["assetMixTwo"]);
            //     if (assetMix === 2) {
            //         args.assetMixOne = results.assetMixTwo;
            //     }
            // }

            // const options =
            //     BestAndWorstGraphGlobal.options && type === "page"
            //         ? BestAndWorstGraphGlobal.options
            //         : getBestAndWorstGraphOptions(args);

            // if (!BestAndWorstGraphGlobal.options && type === "page") {
            //     BestAndWorstGraphGlobal.options = options;
            // }

            // if (!BestAndWorstGraphGlobal.graphAnimator && type === "page") {
            //     BestAndWorstGraphGlobal.graphAnimator = createGraphAnimator(
            //         {
            //             animSpeed: 350
            //         },
            //         canvas
            //     );
            // }

            // canvas.style.width = "100%";
            // context.clearRect(0, 0, canvas.width, canvas.height);

            // if (type === "page") {
            //     if (BestAndWorstGraphGlobal.initialDraw || BestAndWorstGraphGlobal.initialDraw === undefined) {
            //         setTimeout(() => {
            //             BestAndWorstGraphGlobal.graphAnimator(
            //                 options,
            //                 css,
            //                 BestAndWorstGraphGlobal.oldDataset,
            //                 dataset
            //             );
            //             removeAccessibilityTables(options);
            //             drawGraph(canvas, options, css, dataset);
            //             BestAndWorstGraphGlobal.initialDraw = false;
            //             BestAndWorstGraphGlobal.initialTimeout = 0;
            //         }, BestAndWorstGraphGlobal.initialTimeout);
            //     } else {
            //         drawAnimatedGraph(
            //             context,
            //             BestAndWorstGraphGlobal.graphAnimator,
            //             options,
            //             css,
            //             dataset,
            //             dataset,
            //             BestAndWorstGraphGlobal,
            //             true
            //         );
            //         removeAccessibilityTables(options);
            //         drawGraph(canvas, options, css, dataset);
            //     }

            //     $(".best-worst-labels-container, .best-worst-legend").css(
            //         "margin-left",
            //         css.canvasCss.padding.left / devicePixelRatio
            //     );
            // } else {
            //     removeAccessibilityTables(options);
            //     drawGraph(canvas, options, css, dataset);
            // }

            // const titles = {
            //     en: "Best and Worst Years",
            //     fr: "Meilleures et pires années"
            // };
            // const accessibilityTableOptions = { title: titles[lang].split(" ").join("-").toLowerCase() };
            // removeAccessibilityTables(accessibilityTableOptions);
            // drawTable({ accessibilityTableDiv: bestAndWorstGraphTable, options: accessibilityTableOptions });
        }

        let printCanvas = assetMixOneBestAndWorstGraphPrint.get(0);
        printCanvas.height = 368;
        printCanvas.width = 400;
        drawGraphForType({ assetMix: 1, canvas: printCanvas, type: "print" });

        const { assetMixTwo } = results;
        if (assetMixTwo) {
            printCanvas = assetMixTwoBestAndWorstGraphPrint.get(0);
            printCanvas.height = 368;
            printCanvas.width = 400;
            drawGraphForType({ assetMix: 2, canvas: printCanvas, type: "print" });
        }

        const pageCanvas = bestAndWorstGraph.get(0);
        pageCanvas.height = 320 * devicePixelRatio;
        pageCanvas.width = bestAndWorstGraph.closest(".graph-container").outerWidth() * devicePixelRatio;
        drawGraphForType({ canvas: pageCanvas, type: "page" });
    }

    function drawPositiveNegativeReturnsGraphs() {
        function drawCallback() {}

        // function drawTable({ accessibilityTableDiv, assetMix, options: { title }, slices }) {
        //     if (slices.length > 0) {
        //         const { assetMixes } = getInputValues();
        //         const assetMixLabel = assetMixesInfo[assetMixes[assetMix]].label[lang];

        //         const table = document.createElement("table");
        //         table.setAttribute("id", `${title}-table-content`);
        //         let tr = undefined;
        //         let th = undefined;
        //         let td = undefined;
        //         for (let i = 0; i < 2; i++) {
        //             tr = document.createElement("tr");
        //             table.appendChild(tr);
        //             if (i === 0) {
        //                 for (let j = 0; j < slices.length; j++) {
        //                     th = document.createElement("th");
        //                     th.setAttribute("scope", "col");
        //                     th.appendChild(document.createTextNode(slices[j].label));
        //                     tr.appendChild(th);
        //                 }
        //             } else {
        //                 for (let j = 0; j < slices.length; j++) {
        //                     td = document.createElement("td");
        //                     td.appendChild(document.createTextNode(formatter.formatPercentage(slices[j].value)));
        //                     tr.appendChild(td);
        //                 }
        //             }
        //         }
        //         accessibilityTableDiv.appendChild(table);
        //     }
        // }

        function addMouseOverEvent(ctx, config, slices) {
            if (ctx.canvas.mousemove) {
                ctx.canvas.removeEventListener("mousemove", ctx.canvas.mousemove);
            }

            if (ctx.canvas.mouseleave) {
                ctx.canvas.removeEventListener("mouseleave", ctx.canvas.mouseleave);
            }

            let graphContainerClass = ".asset-mix-one-positive-negative-years-graph-container";

            if (ctx.canvas.id.indexOf("one") > -1) {
                PositiveNegativeSlices[0] = [false, false];
            } else {
                PositiveNegativeSlices[1] = [false, false];
                graphContainerClass = ".asset-mix-two-positive-negative-years-graph-container";
            }

            let rafId = null;
            let rafId2 = null;
            const global = ctx.canvas.id.indexOf("one") > -1 ? PositiveNegativeSlices[0] : PositiveNegativeSlices[1];

            ctx.canvas.mousemove = (evt) => {
                const x = evt.offsetX * config.pixelRatio;
                const y = evt.offsetY * config.pixelRatio;
                const paths = config.paths.filter((_, i) => i % 2 !== 0);

                for (let i = 0; i < paths.length; i++) {
                    if (ctx.isPointInPath(paths[i], x, y)) {
                        $(graphContainerClass).next().children().eq(i).addClass("bold");
                        if (!global[i] && !rafId2) {
                            let initialTime = null;
                            global[i] = true;
                            slices[i].offsetr = 0.000001;
                            slices[i].shadowBlur = 10;
                            config.expandOnHover = true;

                            const growSlice = (time) => {
                                if (!initialTime) initialTime = time;
                                const elapsed = time - initialTime || 0.000001;

                                if (elapsed < 200) {
                                    slices[i].offsetr = Math.min((6 * elapsed) / 200, 6);
                                    slices[i].shadowBlur = Math.min((10 * elapsed) / 200, 10);
                                    config.shadow.shadowColour = "rgba(0, 0, 0, 0.5)";

                                    const drawPie = initializePie(ctx.canvas, config, slices);
                                    drawPie(slices);

                                    rafId2 = requestAnimationFrame(growSlice);
                                } else {
                                    slices[i].offsetr = 6;
                                    slices[i].shadowBlur = 10;
                                    config.shadow.shadowColour = "rgba(0, 0, 0, 0.5)";

                                    const drawPie = initializePie(ctx.canvas, config, slices);
                                    drawPie(slices);

                                    rafId2 = null;
                                }
                            };

                            rafId2 = requestAnimationFrame(growSlice);
                        }
                    } else {
                        // Set hovered to false for that particular slice
                        $(graphContainerClass).next().children().eq(i).removeClass("bold");
                        if (slices[i].offsetr && slices[i].offsetr !== 0 && !rafId) {
                            global[i] = false;
                            // slices[i].offsetr = 6;
                            let initialTime = null;

                            const shrinkSlice = (time) => {
                                if (!initialTime) initialTime = time;
                                const elapsed = time - initialTime || 0.000001;

                                if (elapsed < 200) {
                                    slices[i].offsetr = Math.max(6 - (6 * elapsed) / 200, 0);
                                    slices[i].shadowBlur = Math.max(10 - (10 * elapsed) / 200, 0);
                                    config.shadow.shadowColour = "rgba(0, 0, 0, 0.5)";

                                    const drawPie = initializePie(ctx.canvas, config, slices);
                                    drawPie(slices);

                                    rafId = requestAnimationFrame(shrinkSlice);
                                } else {
                                    slices[i].offsetr = 0;
                                    slices[i].shadowBlur = 0;
                                    config.shadow.shadowColour = "rgba(0, 0, 0, 0.5)";

                                    const drawPie = initializePie(ctx.canvas, config, slices);
                                    drawPie(slices);

                                    rafId = null;
                                }
                            };

                            rafId = requestAnimationFrame(shrinkSlice);
                        }
                    }
                }
            };

            ctx.canvas.mouseleave = ({
                altKey,
                bubbles,
                button,
                cancelable,
                clientX,
                clientY,
                ctrlKey,
                detail,
                metaKey,
                relatedTarget,
                screenX,
                screenY,
                shiftKey,
                view
            }) => {
                let newEvent = null;
                if (typeof Event === "function") {
                    newEvent = new Event("mousemove");
                } else {
                    newEvent = document.createEvent("MouseEvents");
                    newEvent.initMouseEvent(
                        "mousemove",
                        bubbles,
                        cancelable,
                        view,
                        detail,
                        screenX,
                        screenY,
                        clientX,
                        clientY,
                        ctrlKey,
                        altKey,
                        shiftKey,
                        metaKey,
                        button,
                        relatedTarget
                    );
                }

                ctx.canvas.dispatchEvent(newEvent);
            };

            ctx.canvas.addEventListener("mousemove", ctx.canvas.mousemove);
            ctx.canvas.addEventListener("mouseleave", ctx.canvas.mouseleave);
        }

        function drawGraphForAssetMix(assetMix) {
            const config = getPositiveNegativeReturnsGraphConfig();

            const { assetMixes } = getInputValues();
            const sliceColourOne = assetMixesInfo[assetMixes[assetMix]].colours.dark;
            const sliceColourTwo = assetMixesInfo[assetMixes[assetMix]].colours.light;
            const assetMixKey = assetMix < 1 ? "assetMixOne" : "assetMixTwo";
            
            const graph = assetMix < 1 ? assetMixOnePositiveNegativeYearsGraph : assetMixTwoPositiveNegativeYearsGraph;
            const canvas = graph.get(0);
            canvas.height = 210;
            canvas.width = 210;
            canvas.style.width = "210px";
            const context = canvas.getContext("2d");

            const sliceImageOne = new Image();
            const sliceImageTwo = new Image();

            function haveAllImagesLoaded(image) {
                return new Promise(resolve => {
                    image.onload = () => resolve();
                })
            };

            if ((sliceColourOne === "#333333" || sliceColourOne === "#6c6f70") && (sliceColourTwo === "#6c6f70" || sliceColourTwo === "#a7a9a9")) {
                Promise.all([sliceImageOne, sliceImageTwo].map((img) => {
                    return haveAllImagesLoaded(img);
                })).then(() => {
                    const slices = getPositiveNegativeReturnsGraphSlices({
                        assetMix: results[assetMixKey],
                        drawCallback,
                        sliceColourOne,
                        sliceColourTwo,
                        sliceImageOne,
                        sliceImageTwo
                    });

                    $(`.${assetMixKey}PositiveValue`).text(formatter.formatPercentage(slices[0].value));
                    $(`.${assetMixKey}NegativeValue`).text(formatter.formatPercentage(slices[1] ? slices[1].value : 0));

                    context.clearRect(0, 0, canvas.width, canvas.height);

                    const drawPie = initializePie(canvas, config, slices);
                    drawPie(slices);

                    const accessibilityTableDiv =
                        assetMix < 1 ? assetMixOnePositiveNegativeReturnsTable : assetMixTwoPositiveNegativeReturnsTable;
                    const titles = {
                        en: "How The Portfolio Performed",
                        fr: "Le rendement du portefeuille"
                    };
                    const options = { title: `${titles[lang].split(" ").join("-").toLowerCase()}-${assetMix + 1}` };
                    removeAccessibilityTables(options);
                    // drawTable({ accessibilityTableDiv, assetMix, options, slices });

                    addMouseOverEvent(context, config, slices);
                })
                sliceImageOne.src = `./common/images/asset-mix-pies/colour-patterns/black-dotted-pattern.png`;
                sliceImageTwo.src = `./common/images/asset-mix-pies/colour-patterns/grey-solid.png`;
            } else {
                const slices = getPositiveNegativeReturnsGraphSlices({
                    assetMix: results[assetMixKey],
                    drawCallback,
                    sliceColourOne,
                    sliceColourTwo
                });

                $(`.${assetMixKey}PositiveValue`).text(formatter.formatPercentage(slices[0].value));
                $(`.${assetMixKey}NegativeValue`).text(formatter.formatPercentage(slices[1] ? slices[1].value : 0));

                context.clearRect(0, 0, canvas.width, canvas.height);

                const drawPie = initializePie(canvas, config, slices);
                drawPie(slices);

                const accessibilityTableDiv =
                    assetMix < 1 ? assetMixOnePositiveNegativeReturnsTable : assetMixTwoPositiveNegativeReturnsTable;
                const titles = {
                    en: "How The Portfolio Performed",
                    fr: "Le rendement du portefeuille"
                };
                const options = { title: `${titles[lang].split(" ").join("-").toLowerCase()}-${assetMix + 1}` };
                removeAccessibilityTables(options);
                // drawTable({ accessibilityTableDiv, assetMix, options, slices });

                addMouseOverEvent(context, config, slices);
            }
        }

        const { assetMixes } = getInputValues();
        for (let i = 0; i < assetMixes.length; i++) {
            drawGraphForAssetMix(i);
        }
    }

    function updatePositiveNegativeReturnsGraphLegends() {
        const { assetMixes, timePeriods } = getInputValues();

        for (let i = 0; i < assetMixes.length; i++) {
            const assetMix = assetMixes[i];
            const bestAndWorstLegend = $(".best-worst-legend > div").eq(i);
            const bestAndWorstLegendCircles = bestAndWorstLegend.find(".circle");
            const performanceContainer = $(".performance-container").eq(i);
            const performanceLegendCircles = performanceContainer.find(".circle");
            let darkHex = assetMixesInfo[assetMix].colours.dark;
            let lightHex = assetMixesInfo[assetMix].colours.light;
            let darkColour = getRGBFromHex(darkHex).join(",");
            let lightColour = getRGBFromHex(lightHex).join(",");
            
            if (darkHex === "#6c6f70" || darkHex === "#333333") {
                bestAndWorstLegendCircles.eq(0).css("background-image", `url(../common/images/asset-mix-pies/colour-patterns/black-dotted-pattern.png)`)
                performanceLegendCircles.eq(0).css("background-image", `url(../common/images/asset-mix-pies/colour-patterns/black-dotted-pattern.png)`)
            } else {
                bestAndWorstLegendCircles.eq(0).css("background-color", `rgb(${darkColour})`);
                bestAndWorstLegendCircles.eq(0).css("background-image", "");
                performanceLegendCircles.eq(0).css("background-color", `rgb(${darkColour})`);
                performanceLegendCircles.eq(0).css("background-image", "");
            }
            if (lightHex === "#6c6f70" || lightHex === "#a7a9a9") {
                bestAndWorstLegendCircles.eq(1).css("background-image", `url(../common/images/asset-mix-pies/colour-patterns/grey-solid.png)`);
                performanceLegendCircles.eq(1).css("background-image", `url(../common/images/asset-mix-pies/colour-patterns/grey-solid.png)`);
            } else {
                bestAndWorstLegendCircles.eq(1).css("background-color", `rgb(${lightColour})`);
                bestAndWorstLegendCircles.eq(1).css("background-image", "");
                performanceLegendCircles.eq(1).css("background-color", `rgb(${lightColour})`);
                performanceLegendCircles.eq(1).css("background-image", "");
            }
        }

        $("[data-id='timePeriod']").text(timePeriods.bestAndWorst);
        $("[data-id='timePeriodLabel']").text(pluralizeText("an", timePeriods.bestAndWorst, false));
    }

    drawBestAndWorstGraph();
    updatePositiveNegativeReturnsGraphLegends();
    drawPositiveNegativeReturnsGraphs();
}

function updateTimeToRecoverTile(results) {
    const firstAssetMixName = document.querySelector(".best-worst-label").innerText;

    function drawCallback({ isHovered, last, lineColour, portfolioValue, screenPixelRatio }) {
        return (context, x, y) => {
            if (last) {
                context.save();

                const { assetMixOne, assetMixTwo } = results;
                const fontSize = 15;
                let yOffset = (fontSize + 5) * screenPixelRatio;

                if (assetMixTwo) {
                    const { portfolioValue: assetMixOnePortfolioValue } = assetMixOne.slice(-1)[0];
                    const { portfolioValue: assetMixTwoPortfolioValue } = assetMixTwo.slice(-1)[0];
                    const minPortfolioValue = Math.min(assetMixOnePortfolioValue, assetMixTwoPortfolioValue);

                    if (portfolioValue <= minPortfolioValue) {
                        yOffset = (fontSize * 2 + 10) * screenPixelRatio;
                    }
                }

                context.fillStyle = "#000000";
                context.textAlign = "end";
                context.textBaseline = "bottom";
                context.font = `${isHovered ? "bold " : ""}${fontSize * screenPixelRatio}px Roobert`;

                const portfolioValueText = formatter.formatCurrency(portfolioValue, 0);
                const textWidth = getTextWidth(portfolioValueText, context.font);
                y = yOffset;

                const path = new Path2D();
                context.fillStyle = "white";
                context.strokeStyle = "transparent";
                if (!isIE()) {
                    path.rect(x - textWidth, y - fontSize * screenPixelRatio, textWidth, fontSize * screenPixelRatio);
                } else {
                    path.moveTo(x - textWidth, y - fontSize * screenPixelRatio);
                    path.lineTo(x, y - fontSize * screenPixelRatio);
                    path.lineTo(x, y);
                    path.lineTo(x - textWidth, y);
                }
                context.fill(path);
                TimeToRecoverGraphGlobal.legendPaths[lineColour] = path;

                context.fillStyle = "#000000";
                context.fillText(portfolioValueText, x, y);

                context.fillStyle = lineColour;
                const radius = 5;
                x = x - textWidth - radius - 10 * screenPixelRatio;
                y = yOffset - (fontSize / 2) * screenPixelRatio;

                context.beginPath();
                context.arc(x, y, radius * screenPixelRatio, 0, 2 * Math.PI);
                context.fill();

                context.restore();
            }
        };
    }

    TimeToRecoverGraphGlobal.drawCallback = drawCallback;

    function drawTable({ accessibilityTableDiv, options: { title } }) {
        const { assetMixOne, assetMixTwo } = results;
        const { assetMixes } = getInputValues();
        const dataLabels = {
            en: {
                threeMonths: "3 months value",
                oneYear: "1 year value",
                threeYear: "3 year value",
                fiveYear: "5 year value",
                recovery: "Recovery time period"
            },
            fr: {
                threeMonths: "Valeur sur 3 mois",
                oneYear: "Valeur sur 1 an",
                threeYear: "Valeur sur 3 ans",
                fiveYear: "Valeur sur 5 ans",
                recovery: "Période de reprise"
            }
        };
        const monthTexts = {
            en: "month",
            fr: "mois"
        };

        let pointsIndices = showPoints("page")(assetMixOne);
        let nonRecoveryMonthDataPoints = assetMixOne.filter(
            ({ recoveryMonth }, index) => pointsIndices.indexOf(index) > -1 && !recoveryMonth
        );
        let [recoveryMonthDataPoint] = assetMixOne.filter(({ recoveryMonth }) => recoveryMonth);
        let tableData = [["", assetMixesInfo[assetMixes[0]].label[lang]]];
        nonRecoveryMonthDataPoints.forEach(({ month, portfolioValue }) => {
            let dataLabel = dataLabels[lang].threeMonths;
            if (month === 15) {
                dataLabel = dataLabels[lang].oneYear;
            } else if (month === 39) {
                dataLabel = dataLabels[lang].threeYear;
            } else if (month === 63) {
                dataLabel = dataLabels[lang].fiveYear;
            }
            tableData = tableData.concat([[dataLabel, formatter.formatCurrency(portfolioValue, 0)]]);
        });
        let recoveryMonthPeriod = Math.ceil(recoveryMonthDataPoint.month - 3);
        recoveryMonthPeriod =
            lang === "en"
                ? `${pluralizeText(monthTexts[lang], recoveryMonthPeriod)}`
                : `${recoveryMonthPeriod} ${monthTexts[lang]}`;
        tableData = tableData.concat([[dataLabels[lang].recovery, recoveryMonthPeriod]]);

        if (assetMixTwo) {
            pointsIndices = showPoints("page")(assetMixTwo);
            nonRecoveryMonthDataPoints = assetMixTwo.filter(
                ({ recoveryMonth }, index) => pointsIndices.indexOf(index) > -1 && !recoveryMonth
            );
            let [recoveryMonthDataPoint] = assetMixTwo.filter(({ recoveryMonth }) => recoveryMonth);
            tableData[0] = tableData[0].concat(assetMixesInfo[assetMixes[1]].label[lang]);
            nonRecoveryMonthDataPoints.forEach(
                ({ portfolioValue }, index) =>
                    (tableData[index + 1] = tableData[index + 1].concat(formatter.formatCurrency(portfolioValue, 0)))
            );
            recoveryMonthPeriod = Math.ceil(recoveryMonthDataPoint.month - 3);
            recoveryMonthPeriod =
                lang === "en"
                    ? `${pluralizeText(monthTexts[lang], recoveryMonthPeriod)}`
                    : `${recoveryMonthPeriod} ${monthTexts[lang]}`;
            const lastIndex = tableData.length - 1;
            tableData[lastIndex] = tableData[lastIndex].concat(recoveryMonthPeriod);
        }

        const table = document.createElement("table");
        table.setAttribute("id", `${title}-table-content`);
        let tr = undefined;
        let th = undefined;
        let td = undefined;
        for (let i = 0; i < tableData.length; i++) {
            const rowData = tableData[i];
            tr = document.createElement("tr");
            table.appendChild(tr);
            for (let j = 0; j < rowData.length; j++) {
                if (i === 0) {
                    th = document.createElement("th");
                    th.setAttribute("scope", "col");
                    if (j === 0) {
                        // th.setAttribute("aria-hidden", "true");
                        th.appendChild(document.createTextNode(lang === "en" ? "Time period and value" : "Période et valeur"));
                    }
                    th.appendChild(document.createTextNode(rowData[j]));
                    tr.appendChild(th);
                } else {
                    if (j === 0) {
                        th = document.createElement("th");
                        th.setAttribute("scope", "row");
                        th.appendChild(document.createTextNode(rowData[j]));
                        tr.appendChild(th);
                    } else {
                        td = document.createElement("td");
                        td.appendChild(document.createTextNode(rowData[j]));
                        tr.appendChild(td);
                    }
                }
            }
        }
        accessibilityTableDiv.appendChild(table);
    }

    function showPoints(type) {
        return (data) => {
            let indices = [];

            if (type === "page") {
                indices = data.reduce(
                    (result, { recoveryMonth, last, month }, index) =>
                        recoveryMonth || last || month === 3 || month === 15 || month === 39
                            ? result.concat(index)
                            : result,
                    indices
                );
            }

            return indices;
        };
    }

    function updateContent({ dataName, dataName2, popup, x, x2 }) {
        const { assetMixOne, assetMixTwo } = results;
        const words = {
            month: {
                en: "month",
                fr: "mois"
            },
            year: {
                en: "year",
                fr: "an"
            }
        };
        const texts = {
            title: {
                en: "month",
                fr: "mois"
            },
            value: {
                en: "to recover to original investment value",
                fr: "pour retrouver la valeur initiale du placement"
            }
        };
        const dataset = TimeToRecoverGraphGlobal.dataset;

        if ($(popup).hasClass("popup-stacked")) {
            let xVal = x;
            let datasetName = dataName;

            const biggestDatasetName = getBiggestDatasetName();
            if (biggestDatasetName !== dataName) {
                xVal = x2;
                datasetName = dataName2;
            }

            let assetMix = assetMixOne;
            if (datasetName !== firstAssetMixName) {
                assetMix = assetMixTwo;
            }

            const word = xVal < 4 ? words.month[lang] : words.year[lang];
            const count = xVal < 4 ? xVal : (xVal - 3) / monthsInAYear;
            const { recoveryMonth, portfolioValue } = assetMix.filter(({ month, recoveryMonth }) =>
                xVal % 1 !== 0 ? recoveryMonth : month === xVal
            )[0];
            let title = lang === "en" || (lang === "fr" && xVal >= 4) ? pluralizeText(word, count) : `${count} ${word}`;
            let value = formatter.formatCurrency(portfolioValue, 0);
            let colour = dataset[1].name === datasetName ? dataset[1].css.colour : dataset[2].css.colour;

            if (recoveryMonth) {
                xVal = Math.ceil(xVal - 3);
                title = lang === "en" ? `${pluralizeText(texts.title[lang], xVal)}` : `${xVal} ${texts.title[lang]}`;
                value = texts.value[lang];
                $(popup).find(".legend-one .circle").hide();
            } else {
                $(popup).find(".legend-one .circle").show();
            }

            $(popup).find("[data-id='titleOne']").text(title);
            $(popup).find(".legend-one .circle").css("background-color", colour);
            $(popup).find("[data-id='assetMixOneValue']").text(value);

            if (datasetName === dataName) {
                datasetName = dataName2;
                xVal = x2;
            } else {
                datasetName = dataName;
                xVal = x;
            }

            if (datasetName !== firstAssetMixName) {
                assetMix = assetMixTwo;
            } else {
                assetMix = assetMixOne;
            }

            const { recoveryMonth: recoveryMonth2, portfolioValue: portfolioValue2 } = assetMix.filter(
                ({ month, recoveryMonth }) => (xVal % 1 !== 0 ? recoveryMonth : month === xVal)
            )[0];
            title = lang === "en" || (lang === "fr" && xVal >= 4) ? pluralizeText(word, count) : `${count} ${word}`;
            value = formatter.formatCurrency(portfolioValue2, 0);
            colour = dataset[1].name === datasetName ? dataset[1].css.colour : dataset[2].css.colour;

            if (recoveryMonth2) {
                xVal = Math.ceil(xVal - 3);
                title = lang === "en" ? `${pluralizeText(texts.title[lang], xVal)}` : `${xVal} ${texts.title[lang]}`;
                value = texts.value[lang];
                $(popup).find(".legend-two .circle").hide();
                $(popup).css("width", "125px");
            } else {
                $(popup).find(".legend-two .circle").show();
                $(popup).css("width", "auto");
            }

            if ($(popup).find("[data-id='titleOne']").text() === title) {
                $(popup).find("[data-id='titleSecond']").hide();
            } else {
                $(popup).find("[data-id='titleSecond']").text(title);
                $(popup).find("[data-id='titleSecond']").show();
            }

            $(popup).find(".legend-two .circle").css("background-color", colour);

            if ($(popup).find("[data-id='assetMixOneValue']").text() === value) {
                $(popup).find("[data-id='assetMixTwoValue']").hide();
            } else {
                $(popup).find("[data-id='assetMixTwoValue']").text(value);
                $(popup).find("[data-id='assetMixTwoValue']").show();
            }
        } else {
            let assetMix = assetMixOne;
            if (dataName !== firstAssetMixName) {
                assetMix = assetMixTwo;
            }

            const word = x < 4 ? words.month[lang] : words.year[lang];
            const count = x < 4 ? x : (x - 3) / monthsInAYear;
            const { recoveryMonth, portfolioValue } = assetMix.filter(({ month }) => month === x)[0];
            let title = lang === "en" || (lang === "fr" && x >= 4) ? pluralizeText(word, count) : `${count} ${word}`;
            let value = formatter.formatCurrency(portfolioValue, 0);
            const colour = dataset[1].name === dataName ? dataset[1].css.colour : dataset[2].css.colour;

            if (recoveryMonth) {
                x = Math.ceil(x - 3);
                title = lang === "en" ? `${pluralizeText(texts.title[lang], x)}` : `${x} ${texts.title[lang]}`;
                value = texts.value[lang];
                $(popup).find(".legend .circle").hide();
            } else {
                $(popup).find(".legend .circle").show();
            }

            $(popup).find("[data-id='title']").text(title);
            $(popup).find(".legend .circle").css("background-color", colour);
            $(popup).find("[data-id='value']").text(value);
        }
    }

    function getBiggestDatasetName() {
        const dataset = TimeToRecoverGraphGlobal.dataset;

        const mixOneLastValue = dataset[1].points[dataset[1].points.length - 1].y;
        const mixTwoLastValue = dataset[2].points[dataset[2].points.length - 1].y;

        if (mixOneLastValue > mixTwoLastValue) {
            return dataset[1].name;
        }

        return dataset[2].name;
    }

    function drawGraphForType({ canvas, type }) {
        let screenPixelRatio = devicePixelRatio;
        let screenWidth = $(window).width();
        if (type === "page") {
            if (screenWidth < 768) {
                screenWidth = "medium";
            } else if (screenWidth < 992) {
                screenWidth = "large";
            } else {
                screenWidth = "regular";
            }

            canvas.style.width = "100%";
        } else {
            screenPixelRatio = 1;
            screenWidth = "regular";
        }

        const context = canvas.getContext("2d");

        const css = (TimeToRecoverGraphGlobal.css = getTimeToRecoverGraphCSS({ screenPixelRatio, screenWidth }));

        const { investmentAmount, timePeriods } = getInputValues();
        const lineColourOne = $("#time-to-recover-panel .circle-light").css("background-color");
        const lineColourTwo = $("#time-to-recover-panel .circle-dark").css("background-color");
        const dataset =
            TimeToRecoverGraphGlobal.dataset && type === "page"
                ? TimeToRecoverGraphGlobal.dataset
                : getTimeToRecoverGraphDataset({
                      ...results,
                      canvas,
                      drawCallback,
                      investmentAmount,
                      lineColourOne,
                      lineColourTwo,
                      page: type === "page",
                      screenPixelRatio,
                      showPoints: showPoints(type),
                      timePeriod: timePeriods.timeToRecover
                  });

        if (!TimeToRecoverGraphGlobal.dataset && type === "page") {
            TimeToRecoverGraphGlobal.dataset = dataset;
        }

        const popupOne = timeToRecoverGraph.closest(".graph-container").find(".popup-one");
        const popupStacked = timeToRecoverGraph.closest(".graph-container").find(".popup-stacked");
        const options =
            TimeToRecoverGraphGlobal.options && type === "page"
                ? TimeToRecoverGraphGlobal.options
                : getTimeToRecoverGraphOptions({
                      ...results,
                      canvas,
                      css,
                      popupOne,
                      popupStacked,
                      updateContent,
                      screenPixelRatio,
                      screenWidth,
                      timePeriod: timePeriods.timeToRecover
                  });

        if (!TimeToRecoverGraphGlobal.options && type === "page") {
            TimeToRecoverGraphGlobal.options = options;
        }

        if (!TimeToRecoverGraphGlobal.graphAnimator && type === "page") {
            TimeToRecoverGraphGlobal.graphAnimator = createGraphAnimator(
                {
                    animSpeed: 250
                },
                canvas
            );
        }

        context.clearRect(0, 0, canvas.width, canvas.height);
        if (type === "page") {
            if (TimeToRecoverGraphGlobal.initialDraw || TimeToRecoverGraphGlobal.initialDraw === undefined) {
                TimeToRecoverGraphGlobal.graphAnimator = createGraphAnimator(
                    {
                        animSpeed: 10,
                        leftToRightAnimation: true
                    },
                    canvas
                );
                setTimeout(() => {
                    TimeToRecoverGraphGlobal.graphAnimator(options, css, TimeToRecoverGraphGlobal.oldDataset, dataset);
                    drawGraph(canvas, options, css, dataset);
                    TimeToRecoverGraphGlobal.initialDraw = false;
                    TimeToRecoverGraphGlobal.initialTimeout = 0;
                    TimeToRecoverGraphGlobal.reanimateAllLines = false;
                    TimeToRecoverGraphGlobal.graphAnimator = createGraphAnimator(
                        {
                            animSpeed: 250
                        },
                        canvas
                    );
                }, TimeToRecoverGraphGlobal.initialTimeout);
            } else {
                drawAnimatedGraph(
                    context,
                    TimeToRecoverGraphGlobal.graphAnimator,
                    options,
                    css,
                    dataset,
                    dataset,
                    TimeToRecoverGraphGlobal,
                    true
                );
                drawGraph(canvas, options, css, dataset);
            }
            TimeToRecoverGraphGlobal.previousColours = [lineColourOne];
            if (results.assetMixTwo) {
                TimeToRecoverGraphGlobal.previousColours.push(lineColourTwo);
            }
        } else {
            drawGraph(canvas, options, css, dataset);
        }

        if (type === "page") {
            const titles = {
                en: "Time to Recover",
                fr: "Du temps pour récupérer"
            };
            const accessibilityTableOptions = { title: titles[lang].split(" ").join("-").toLowerCase() };
            removeAccessibilityTables(accessibilityTableOptions);
            drawTable({ accessibilityTableDiv: timeToRecoverGraphTable, options: accessibilityTableOptions });
        }
    }

    const printCanvas = timeToRecoverGraphPrint.get(0);
    printCanvas.height = 420;
    printCanvas.width = 863;
    drawGraphForType({ canvas: printCanvas, type: "print" });

    const pageCanvas = timeToRecoverGraph.get(0);
    pageCanvas.height = 420 * devicePixelRatio;
    pageCanvas.width = timeToRecoverGraph.closest(".graph-container").outerWidth() * devicePixelRatio;
    drawGraphForType({ canvas: pageCanvas, type: "page" });
}

function adjustWrapperAssetsOrder() {
    const screenWidth = $(window).width();
    const bestAndWorstWrapperAsset = $(".best-and-worst-wrapper-asset");
    const performanceWrapperAsset = $(".performance-wrapper-asset");

    // if (screenWidth < 768) {
    //     performanceWrapperAsset.before(bestAndWorstWrapperAsset);
    // } else {
    //     bestAndWorstWrapperAsset.before(performanceWrapperAsset);
    // }
}

function setUpWindowResizing() {
    const resizeDebounced = debounce(() => {
        if (
            window.CSS &&
            window.CSS.supports &&
            (window.CSS.supports("position", "sticky") || window.CSS.supports("position", "-webkit-sticky"))
        ) {
            if (clientWidth < 768) {
                $(document).off("scroll", handleScrollThrottle);
                $("#asset-input-values").removeClass("collapsed").addClass("expanded");
            } else {
                $(document).on("scroll", handleScrollThrottle).trigger("scroll");
            }
        }

        adjustWrapperAssetsOrder();
        clearGlobalsForRedraw(true);
        updateTiles();
    }, 400);

    $(window).on("resize", () => {
        if (document.documentElement.clientWidth === clientWidth) {
            return false;
        }

        clientWidth = document.documentElement.clientWidth;

        resizeDebounced();
    });
}

function updatePrintValues({ investmentGrowth, bestAndWorst, timeToRecover }) {
    const { assetMixes, investmentAmount, timePeriods } = getInputValues();
    const investmentGrowthData = { ...investmentGrowth, graphSrc: investmentGrowthGraphPrint.get(0).toDataURL() };
    const timeToRecoverData = { ...timeToRecover, graphSrc: timeToRecoverGraphPrint.get(0).toDataURL() };

    let bestAndWorstData = {
        assetMixOne: {
            ...bestAndWorst.assetMixOne,
            graphSrc: assetMixOneBestAndWorstGraphPrint.get(0).toDataURL(),
            pieSrc: assetMixOnePositiveNegativeYearsGraph.get(0).toDataURL(),
            piePositiveValue: document.querySelector(".assetMixOnePositiveValue").innerText,
            pieNegativeValue: document.querySelector(".assetMixOneNegativeValue").innerText
        }
    };

    if (assetMixes.length > 1) {
        bestAndWorstData = {
            ...bestAndWorstData,
            assetMixTwo: {
                ...bestAndWorst.assetMixTwo,
                graphSrc: assetMixTwoBestAndWorstGraphPrint.get(0).toDataURL(),
                pieSrc: assetMixTwoPositiveNegativeYearsGraph.get(0).toDataURL(),
                piePositiveValue: document.querySelector(".assetMixTwoPositiveValue").innerText,
                pieNegativeValue: document.querySelector(".assetMixTwoNegativeValue").innerText
            }
        };
    }

    storePrintValues({
        assetMixes,
        assetMixesInfo,
        investmentAmount,
        investmentGrowth: investmentGrowthData,
        bestAndWorst: bestAndWorstData,
        timePeriods,
        timeToRecover: timeToRecoverData
    });
}

function clearGlobalsForRedraw(
    resize = false,
    reanimateAllLines = false,
    tiles = ["investmentGrowth", "bestAndWorst", "timeToRecover"]
) {
    if (tiles.indexOf("investmentGrowth") > -1) {
        InvestmentGrowthGraphGlobal.dataset = undefined;
        InvestmentGrowthGraphGlobal.options = undefined;
        InvestmentGrowthGraphGlobal.initialDraw = !resize;
        InvestmentGrowthGraphGlobal.reanimateAllLines = reanimateAllLines;
        if (reanimateAllLines) {
            InvestmentGrowthGraphGlobal.previousColours = [];
        }
        InvestmentGrowthGraphGlobal.legendPaths = {};
    }

    if (tiles.indexOf("bestAndWorst") > -1) {
        BestAndWorstGraphGlobal.dataset = undefined;
        BestAndWorstGraphGlobal.options = undefined;
        BestAndWorstGraphGlobal.initialDraw = !resize;
    }

    if (tiles.indexOf("timeToRecover") > -1) {
        TimeToRecoverGraphGlobal.dataset = undefined;
        TimeToRecoverGraphGlobal.options = undefined;
        TimeToRecoverGraphGlobal.initialDraw = !resize;
        TimeToRecoverGraphGlobal.reanimateAllLines = reanimateAllLines;
        if (reanimateAllLines) {
            TimeToRecoverGraphGlobal.previousColours = [];
        }
        TimeToRecoverGraphGlobal.legendPaths = {};
    }
}

function setupSticky() {
    if (
        window.CSS &&
        window.CSS.supports &&
        (window.CSS.supports("position", "sticky") || window.CSS.supports("position", "-webkit-sticky"))
    ) {
        return;
    }

    const sticky = $(".sticky")[0];
    const spacer = document.createElement("div");
    spacer.style.position = "relative";
    spacer.style.maxWidth = "1200px";
    spacer.style.margin = "0 auto";

    sticky.parentElement.insertBefore(spacer, sticky);
    spacer.appendChild(sticky);

    resetSticky();
    sticky.style.position = "absolute";
    sticky.style.top = "0px";

    function resetSticky() {
        const rectSticky = sticky.getBoundingClientRect();
        const rectSpacer = spacer.getBoundingClientRect();
        spacer.style.height = rectSticky.height + "px";
        if (rectSpacer.top < 0) {
            sticky.style.position = "fixed";
            sticky.style.top = "55px";
            $(".asset-input-container").width($(".asset-input-container").parent().width());
            $(".asset-input-container .card-row").css("margin-right", "0px");
            if (collapsePanelOnSticky) {
                $("#asset-input-values").removeClass("expanded").addClass("collapsed");
            } else {
                $("#asset-input-values").removeClass("collapsed").addClass("expanded");
            }

            $(".arrow-asset-container").addClass("show");
        } else {
            sticky.style.position = "absolute";
            sticky.style.top = "0px";
            $("#asset-input-values").removeClass("collapsed").addClass("expanded");
            $(".arrow-asset-container").removeClass("show");
        }
    }

    window.setInterval(resetSticky, 100);
    window.addEventListener("scroll", resetSticky);
}

function unhideIcon() {
    $("#pgNavTaggle").on("click", () => {
        $(".fa-chevron-up").css("display", "");
    });
}

$(() => {
    if (window.location.search === "?pdf") {
        setupPreview();
        return;
    }

    adjustWrapperAssetsOrder();
    setupSticky();
    resetForms();
    setUpInputs();
    setUpAssetMixCheckboxes();
    setUpAssetMixInputsPanel();
    updateAssetMixPiesAndTexts();
    updateTiles();
    setUpWindowResizing();
    setUpButtons();
    unhideIcon();
});
